/**
 * Generic share-by-link primitive + the first surface built on it (a
 * read-only, client-facing project status page).
 *
 * Trust model
 * -----------
 * The token in the URL is the entire credential. It is 256 bits of
 * `crypto.randomBytes` and is stored ONLY as a SHA-256 digest, so a database
 * dump yields no working links (this is the PortalMagicLink rule, applied to
 * a long-lived link instead of a one-shot one).
 *
 * A link grants strictly what the creator ticked. Capability maps are SPARSE
 * and read with `=== true`, so an absent key is always a denial and the
 * narrowest projection is the default. The public endpoint reads NO query
 * parameters — the projection can only be widened by editing the stored row.
 */

// ---------------------------------------------------------------------------
// The primitive
// ---------------------------------------------------------------------------

/** Every module a share link may point at (extend deliberately, per surface). */
export type ShareLinkModuleType = 'PROJECT'

/** Sparse capability map: present-and-true grants, anything else denies. */
export type ShareLinkCapabilities = Record<string, boolean>

/**
 * What the CREATOR sees about a link. Deliberately does not carry the token
 * or its hash — the plaintext is returned exactly once, at creation.
 */
export interface ShareLinkSummary {
  id: string
  moduleType: ShareLinkModuleType
  moduleId: string
  /** First 8 chars of the token — identifies the link, cannot open it. */
  tokenPrefix: string
  label: string | null
  capabilities: ShareLinkCapabilities
  expiresAt: string | null
  revokedAt: string | null
  accessCount: number
  lastAccessedAt: string | null
  createdAt: string
  /** Derived server-side: not revoked and not past expiry. */
  isLive: boolean
}

/** Creation response — the ONLY time the plaintext token exists server-side. */
export interface ShareLinkCreated {
  link: ShareLinkSummary
  /** Show once. Scrambl cannot show it again. */
  url: string
}

// ---------------------------------------------------------------------------
// Project status pages
// ---------------------------------------------------------------------------

/**
 * Capabilities for a project status share. Each one widens the projection by
 * exactly one section and nothing else. There is no capability that reveals
 * money, internal discussion, or anybody's contact details — those are not
 * gated, they are absent from the read model entirely.
 */
export const PROJECT_STATUS_SHARE_CAPABILITY_KEYS = [
  'showDescription',
  'showPhases',
  'showCompletedWork',
  'showUpcomingWork',
  'showTeam',
] as const

export type ProjectStatusShareCapabilityKey =
  (typeof PROJECT_STATUS_SHARE_CAPABILITY_KEYS)[number]

export type ProjectStatusShareCapabilities = Record<
  ProjectStatusShareCapabilityKey,
  boolean
>

/**
 * Defaults for a NEW link (what the create dialog starts with). Note these are
 * defaults for the UI, not fallbacks for reading a stored row — reading always
 * treats a missing key as `false`.
 */
export const DEFAULT_PROJECT_STATUS_SHARE_CAPABILITIES: ProjectStatusShareCapabilities =
  {
    showDescription: false,
    showPhases: true,
    showCompletedWork: true,
    showUpcomingWork: true,
    showTeam: false,
  }

/**
 * Longest life a status link may be given, in days. The server enforces it;
 * the create dialog uses it to bound its own input.
 */
export const MAX_SHARE_LINK_DAYS = 365

/**
 * What the creator POSTs to mint a link.
 *
 * Validation and the fail-closed capability resolver deliberately live on the
 * SERVER (`apps/api/src/share-link/projection-contract.ts`) — they are what
 * stands between a token and somebody's project, and a client-side copy would
 * only be decoration. The server's capability map is typed against
 * `ProjectStatusShareCapabilityKey` above, so the two cannot drift silently.
 */
export interface CreateProjectStatusShareDto {
  label?: string
  /** Days until expiry; null/omitted = lives until revoked. */
  expiresInDays?: number | null
  capabilities: ProjectStatusShareCapabilities
}

/** Normalised project state — never the raw DB string. */
export type PublicProjectState = 'ACTIVE' | 'COMPLETED' | 'ON_HOLD' | 'CANCELED'

export interface PublicProjectPhase {
  name: string
  state: 'PLANNED' | 'ACTIVE' | 'COMPLETED'
  startDate: string | null
  endDate: string | null
}

/** A single piece of work. Name + date only — no ids, no assignee, no notes. */
export interface PublicProjectWorkItem {
  name: string
  /** completedAt for finished work, dueDate for upcoming work. May be null. */
  date: string | null
}

/** A teammate, at the resolution a client actually needs. */
export interface PublicProjectTeamMember {
  /** "Ada R." — first name plus last initial. Never an email or a user id. */
  displayName: string
  /** Job title if the org set one, else null. Never a rate or cost. */
  roleTitle: string | null
}

export interface PublicProjectProgress {
  totalItems: number
  completedItems: number
  /** 0-100, integer. */
  percentComplete: number
}

/**
 * THE PROJECTION. This interface is the contract: whatever is not a field here
 * cannot reach a link holder. Adding a field is a deliberate act with a
 * matching entry in PROJECT_STATUS_PROJECTION_MANIFEST below.
 */
export interface PublicProjectStatus {
  organizationName: string
  projectName: string
  /** Present only when the `showDescription` capability is on. */
  description: string | null
  state: PublicProjectState
  stateLabel: string
  startDate: string | null
  targetDate: string | null
  progress: PublicProjectProgress
  /** Empty when the matching capability is off. */
  phases: PublicProjectPhase[]
  recentlyCompleted: PublicProjectWorkItem[]
  upcoming: PublicProjectWorkItem[]
  team: PublicProjectTeamMember[]
  /** When this snapshot was built. */
  generatedAt: string
  /** Echoed so the page can label empty sections honestly. */
  capabilities: ProjectStatusShareCapabilities
}

/**
 * Creator-facing description of the projection, rendered in the share dialog
 * so nobody has to trust a sentence in a tooltip. Kept next to the interface
 * on purpose: if the projection grows, this list is in the same diff.
 */
export const PROJECT_STATUS_PROJECTION_MANIFEST: {
  included: { label: string; capability?: ProjectStatusShareCapabilityKey }[]
  excluded: string[]
} = {
  included: [
    { label: 'Your organization name and the project name' },
    { label: 'Project state (in progress / on hold / complete) and dates' },
    {
      label:
        'Overall progress: how many work items are done, as a count and a percentage',
    },
    { label: 'Project description', capability: 'showDescription' },
    {
      label: 'Phase names, their state, and their dates',
      capability: 'showPhases',
    },
    {
      label:
        'Names and completion dates of up to 10 items finished in the last 30 days',
      capability: 'showCompletedWork',
    },
    {
      label: 'Names and due dates of the next 10 unfinished items',
      capability: 'showUpcomingWork',
    },
    {
      label: 'Team first names with last initial, plus job title',
      capability: 'showTeam',
    },
  ],
  excluded: [
    'Any money: budgets, fees, costs, rates, margins, invoices, contracts, quotes',
    'Time tracked, estimates, story points, or velocity',
    'Comments, notes, discussions, documents, and attachments',
    'Email addresses, phone numbers, avatars, and user accounts',
    'Task descriptions, task numbers, ids, tags, and custom fields',
    'Private tasks, archived tasks, and deleted tasks',
    'Anything about any other project, client, or record',
  ],
}
